import { zodResolver } from '@hookform/resolvers/zod';
import { map } from 'ramda';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import BackAndNextNav from '@ui/molecules/BackAndNextNav';
import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import FineprintBox from '@ui/molecules/FineprintBox';
import ReadMore from '@ui/molecules/ReadMore';

import flexible from '@ui/styled/Flexible';
import padded from '@ui/styled/Padded';
import spaced from '@ui/styled/Spaced';

import { commonErrors } from '@utils/error';
import { requiredCheckbox } from '@utils/validation';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';

interface IInformedConsentItem {
	itemDetails: string;
	itemTitle?: string;
}

export interface IInformedConsentStepPageContent {
	pageHeader: string;

	termsTitle: string;
	termsContent: string;
	termsFinePrint: string;
	termsAgreeLabel: string;

	benefitsTitle: string;
	risksTitle: string;
	alternativesTitle: string;

	benefits: Array<IInformedConsentItem>;
	risks: Array<IInformedConsentItem>;
	alternatives: Array<IInformedConsentItem>;

	downloadCopyContent: string;
	learnMoreContent: string;
}

interface IInformedConsentStepProps extends IFlowStepComponentProps {
	pageContent: IInformedConsentStepPageContent;
}

const PaddedFlexColumn = padded(flexible(FlexColumn));
const SpacedFlexColumn = spaced(flexible(FlexColumn));
const SpacedGridItem = spaced(CenteredGridItem);
const SpacedBasicText = spaced(BasicText);

const InformedConsentStep: FC<IInformedConsentStepProps> = ({
	existingFormData,
	handleFormSubmission = () => {},
	handleNavBack = () => {},
	pageContent,
	stepKey,
}) => {
	const {
		pageHeader,
		termsTitle,
		termsContent,
		termsFinePrint,
		termsAgreeLabel,
		downloadCopyContent,
		learnMoreContent,
		benefitsTitle,
		risksTitle,
		alternativesTitle,
		benefits,
		risks,
		alternatives,
	} = pageContent;

	const AGREES_TO_TERMS = 'agreesToTerms';
	const schema = z.object({
		[AGREES_TO_TERMS]: requiredCheckbox(commonErrors.checkRequired),
	});
	type Schema = Partial<z.input<typeof schema>>;
	const [submitting, setSubmitting] = useState(false);

	const {
		handleSubmit,
		register,
		formState: { errors },
	} = useForm<Schema>({
		defaultValues: existingFormData,
		resolver: zodResolver(schema),
	});
	const onSubmit = async (data: Record<string, unknown>) => {
		setSubmitting(true);
		await handleFormSubmission(data, stepKey);
		setSubmitting(false);
	};

	return (
		<SpacedGridItem marginBottom="xxxl">
			<form onSubmit={handleSubmit(onSubmit)}>
				<SpacedFlexColumn marginBottom="xxl" gap="lgxl">
					<HeadingText>{pageHeader}</HeadingText>
					<FineprintBox
						finePrint={termsFinePrint}
						checkbox
						checkboxProps={{
							label: termsAgreeLabel,
							alignment: 'flex-start',
							errorLabel: errors?.agreesToTerms?.message && errors.agreesToTerms.message,
							...register(AGREES_TO_TERMS),
						}}>
						<BasicText variant="field">{termsTitle}</BasicText>
						<WYSIWYGContent dangerouslySetInnerHTML={{ __html: termsContent }} />
					</FineprintBox>
					<div>
						<ReadMore defaultOpen={false} label={benefitsTitle}>
							<PaddedFlexColumn paddingTop="sm" gap="lg">
								{!!benefits &&
									map(
										({ itemTitle, itemDetails }) => (
											<div>
												{!!itemTitle && (
													<SpacedBasicText variant="field" marginBottom="sm">
														{itemTitle}
													</SpacedBasicText>
												)}
												<WYSIWYGContent
													dangerouslySetInnerHTML={{
														__html: itemDetails,
													}}
												/>
											</div>
										),
										benefits
									)}
							</PaddedFlexColumn>
						</ReadMore>
					</div>
					<div>
						<ReadMore defaultOpen={false} label={risksTitle}>
							<PaddedFlexColumn paddingTop="sm" gap="lg">
								{!!risks &&
									map(
										({ itemTitle, itemDetails }) => (
											<div>
												{!!itemTitle && (
													<SpacedBasicText variant="field" marginBottom="sm">
														{itemTitle}
													</SpacedBasicText>
												)}
												<WYSIWYGContent
													dangerouslySetInnerHTML={{
														__html: itemDetails,
													}}
												/>
											</div>
										),
										risks
									)}
							</PaddedFlexColumn>
						</ReadMore>
					</div>
					<div>
						<ReadMore defaultOpen={false} label={alternativesTitle}>
							<PaddedFlexColumn paddingTop="sm" gap="lg">
								{!!alternatives &&
									map(
										({ itemTitle, itemDetails }) => (
											<div>
												{!!itemTitle && (
													<SpacedBasicText variant="field" marginBottom="sm">
														{itemTitle}
													</SpacedBasicText>
												)}
												<WYSIWYGContent
													dangerouslySetInnerHTML={{
														__html: itemDetails,
													}}
												/>
											</div>
										),
										alternatives
									)}
							</PaddedFlexColumn>
						</ReadMore>
					</div>
					<WYSIWYGContent dangerouslySetInnerHTML={{ __html: downloadCopyContent }} />
					<WYSIWYGContent dangerouslySetInnerHTML={{ __html: learnMoreContent }} />
				</SpacedFlexColumn>
				<BackAndNextNav
					marginTop="xxl"
					marginBottom="xxxl"
					submitLabel="Next"
					backOnClick={() => handleNavBack(stepKey)}
					submitting={submitting}
				/>
			</form>
		</SpacedGridItem>
	);
};

export default InformedConsentStep;
